import * as React from 'react';

import { ChartRoot } from '../core/ChartRoot';
import {
  collectDomain,
  collectStackedValues,
  collectValues,
  toNumericSeries
} from '../core/scales';
import type { ChartMargin, ChartSeries, ChartValueFormatter, XScaleKind } from '../core/types';
import { useChartTooltip } from '../core/use-chart-tooltip';
import { Bars } from '../primitives/Bars';
import { Grid } from '../primitives/Grid';
import { HoverBands } from '../primitives/HoverBands';
import { Legend } from '../primitives/Legend';
import { LinePath } from '../primitives/LinePath';
import { ChartTooltip } from '../primitives/Tooltip';
import { XAxis } from '../primitives/XAxis';
import { YAxis } from '../primitives/YAxis';

export interface CartesianChartProps {
  series: ChartSeries[];
  height?: number;
  /** Which marks to draw — `both` overlays a line on the bars */
  marks: 'bars' | 'line' | 'area' | 'both';
  /** X-scale kind — categorical band (default), linear numbers, or ISO time */
  xScaleKind?: XScaleKind;
  /** Stacks bar series on top of each other instead of grouping */
  stacked?: boolean;
  /** Formats tooltip values */
  formatValue: ChartValueFormatter;
  /** Formats left y-axis ticks — defaults to `formatValue` */
  formatTick?: (value: number) => string;
  /** Formats right y-axis ticks — defaults to `formatTick` */
  formatTickRight?: (value: number) => string;
  /** Formats x-axis labels (categories, numbers, or ISO dates) */
  formatCategory?: (value: string | number) => string;
  showLegend?: boolean;
  showGrid?: boolean;
  axisLine?: boolean;
  /** Highlights the hovered x-band background */
  highlight?: boolean;
  /** Rotates x labels (degrees, e.g. -45) for long categories */
  xLabelAngle?: number;
  margin?: Partial<ChartMargin>;
  bandPadding?: number;
  maxBarSize?: number;
  barRadius?: number;
  getBarColor?: (value: number, x: string | number, index: number) => string;
  /** Overrides the tooltip row label, e.g. a suffixed metric name */
  tooltipLabel?: (seriesLabel: string) => string;
  ariaLabel?: string;
  className?: string;
}

/** Bar / line / area / composed chart over one x-scale with up to two y-scales. */
export const CartesianChart = ({
  series,
  height = 240,
  marks,
  xScaleKind = 'band',
  stacked = false,
  formatValue,
  formatTick,
  formatTickRight,
  formatCategory,
  showLegend = false,
  showGrid = true,
  axisLine = true,
  highlight = false,
  xLabelAngle,
  margin,
  bandPadding = 0.2,
  maxBarSize,
  barRadius,
  getBarColor,
  tooltipLabel,
  ariaLabel,
  className
}: CartesianChartProps) => {
  const domain = React.useMemo(() => collectDomain(series, xScaleKind), [series, xScaleKind]);
  const numeric = React.useMemo(() => toNumericSeries(series, domain), [series, domain]);
  const isBarChart = marks !== 'line' && marks !== 'area';
  const values = React.useMemo(
    () =>
      stacked && isBarChart
        ? collectStackedValues(numeric, 'left')
        : collectValues(numeric, 'left'),
    [numeric, stacked, isBarChart]
  );
  const rightSeries = React.useMemo(() => numeric.filter(s => s.axis === 'right'), [numeric]);
  const valuesRight = React.useMemo(
    () => (rightSeries.length > 0 ? collectValues(numeric, 'right') : undefined),
    [numeric, rightSeries.length]
  );
  const { tooltip, show, hide } = useChartTooltip();
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);

  const handleHover = (index: number | null, position: { x: number; y: number }): void => {
    setActiveIndex(index);
    if (index === null) {
      hide();
      return;
    }

    const entries = numeric
      .filter(s => s.values[index] !== undefined)
      .map(s => ({
        label: tooltipLabel ? tooltipLabel(s.label) : s.label,
        value: formatValue(s.values[index] as number, s.currency),
        color: s.color
      }));

    const title = formatCategory ? formatCategory(domain[index]) : String(domain[index]);
    show({ x: position.x, y: position.y, title, entries });
  };

  // In composed mode the overlay line covers all series; bars stay on the left axis.
  const barSeries = marks === 'both' ? numeric.filter(s => s.axis === 'left') : numeric;

  return (
    <div className={className}>
      <ChartRoot
        domain={domain}
        values={values}
        valuesRight={valuesRight}
        xScaleKind={xScaleKind}
        height={height}
        margin={margin}
        bandPadding={bandPadding}
        ariaLabel={ariaLabel}
        overlay={<ChartTooltip state={tooltip} />}
      >
        {showGrid && <Grid />}
        <YAxis format={formatTick ?? (value => formatValue(value))} />
        {valuesRight && (
          <YAxis
            side='right'
            format={formatTickRight ?? formatTick ?? (value => formatValue(value))}
          />
        )}
        <XAxis format={formatCategory} axisLine={axisLine} labelAngle={xLabelAngle} />
        {isBarChart && (
          <Bars
            series={barSeries}
            radius={barRadius}
            maxBarSize={maxBarSize}
            getBarColor={getBarColor}
            stacked={stacked}
          />
        )}
        {(marks === 'line' || marks === 'area' || marks === 'both') && (
          <LinePath
            series={numeric}
            dots={marks !== 'both'}
            area={marks === 'area'}
            activeIndex={activeIndex}
            strokeWidth={marks === 'both' ? 2 : 2.5}
            strokeOpacity={marks === 'both' ? 0.6 : undefined}
            stroke={marks === 'both' ? 'var(--color-foreground-muted)' : undefined}
          />
        )}
        <HoverBands onHover={handleHover} activeIndex={activeIndex} highlight={highlight} />
      </ChartRoot>
      {showLegend && <Legend items={numeric.map(s => ({ label: s.label, color: s.color }))} />}
    </div>
  );
};

CartesianChart.displayName = 'CartesianChart';
